<script setup>
import { useField } from 'vee-validate'
import { computed } from 'vue'

const emit = defineEmits(['update'])

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validators: value => ['sm', 'md', 'lg'].includes(value),
  },
  label: {
    type: String,
    required: false,
  },
  mask: {
    type: String,
    default: 'money_reais',
    validators: value => ['money_reais', 'percentage', 'new_percentage'].includes(value),
  },
  name: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  allowBlank: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel({ type: [Number, String], default: null })

const { value, errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
  validateOnValueUpdate: false,
  initialValue: model.value ?? (props.allowBlank ? null : 0),
})

const masks = {
  moneyReais: {
    decimal: ',',
    thousands: '.',
    prefix: 'R$ ',
    precision: 2,
    masked: true,
  },
  percentage: {
    decimal: ',',
    thousands: '',
    suffix: ' %',
    precision: 2,
    masked: true,
  },
  newPercentage: {
    decimal: ',',
    thousands: '',
    suffix: '%',
    precision: 1,
    masked: true,
  },
}

const maskType = computed(() => ({
  ...(masks[props.mask] || masks.moneyReais),
  allowBlank: props.allowBlank,
}))

const handleMoneyUpdate = newValue => {
  value.value = newValue
  model.value = newValue

  emit('update', {
    name: props.name,
    value: newValue,
  })
}
</script>

<template>
  <div
    class="flex w-full flex-col gap-1 px-0.5 py-0"
    :class="{
      'text-neutral-40': disabled,
    }"
  >
    <label
      class="text-neutral-60"
      :class="{
        'text-base': size === 'lg',
        'text-sm': size === 'md',
        'text-xs': size === 'sm',
      }"
      :for="$attrs.id"
    >
      {{ label }}
    </label>
    <div class="flex flex-row">
      <money3
        class="placeholder:text-neutral-40 w-full rounded-lg"
        :class="{
          error: errorMessage,
          input: !errorMessage,
          'h-[38px]': size === 'md',
          'h-[44px]': size === 'lg',
          'h-[36px]': size === 'sm',
          'border-neutral-30': model === '000',
          'border-neutral-40': model !== '000',
        }"
        :name="name"
        :disabled="disabled"
        :placeholder="placeholder"
        v-model.number="value"
        v-bind="maskType"
        @update:modelValue="handleMoneyUpdate"
      />
    </div>
    <span class="text-error-60 text-xs" id="value-errors" v-html="errorMessage"></span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40 text-primary-90;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
